/**
 * Opt-in product tour for HospiceGuard.
 *
 * Launched ON DEMAND from the "Take a tour" button in the header — it never
 * auto-starts. A lightweight custom implementation (no reactour positioning
 * quirks): a fixed bottom-right card that is always on screen, plus a spotlight
 * ring drawn from the target's live bounding box. The spotlight overlay is
 * pointer-events:none, so every click passes straight through to the app and
 * the tour can NEVER block navigation. It also auto-closes on any route change.
 */
import { createContext, useContext, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Compass, SkipForward, X } from 'lucide-react';

const TourCtx = createContext(null);

const STEPS = [
  {
    selector: '[data-testid="logo-link"]',
    title: 'Welcome to HospiceGuard 🏥',
    body: 'Every tool — SSVI intelligence, documentation, pre-audit checks, audit defense and analytics — lives in the sidebar here on the left, grouped for quick access.',
  },
  {
    selector: '[data-testid="video-tour-btn"]',
    title: 'Training Videos',
    body: 'Open the built-in video library any time to watch step-by-step walkthroughs of each feature.',
  },
  {
    selector: '[data-testid="dark-mode-toggle"]',
    title: 'Light & Dark Mode',
    body: 'Switch the interface between light and dark whenever you like — great for long charting sessions.',
  },
  {
    selector: '[data-testid="user-menu-trigger"]',
    title: 'Your Account',
    body: 'Manage your profile, jump into settings, and sign out securely from this menu.',
  },
  {
    selector: '[data-testid="take-tour-btn"]',
    title: "You're all set! 🎉",
    body: 'You can replay this tour any time from the "Take a tour" button here. Pick any tool in the sidebar to get started.',
  },
];

const Spotlight = ({ selector }) => {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    const el = selector ? document.querySelector(selector) : null;
    if (!el) {
      setRect(null);
      return;
    }
    try { el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_e) {}
    const update = () => setRect(el.getBoundingClientRect());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const id = setInterval(update, 300);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      clearInterval(id);
    };
  }, [selector]);

  if (!rect) {
    // No target found — dim the screen lightly so the card still reads as a tour.
    return (
      <div
        className="fixed inset-0 z-[99998]"
        style={{ pointerEvents: 'none', background: 'rgba(2,6,23,0.45)' }}
        data-testid="tour-spotlight"
      />
    );
  }

  return (
    <div
      className="fixed z-[99998] rounded-xl transition-all duration-200"
      style={{
        pointerEvents: 'none', // clicks pass straight through — never blocks navigation
        left: rect.left - 6,
        top: rect.top - 6,
        width: rect.width + 12,
        height: rect.height + 12,
        boxShadow: '0 0 0 9999px rgba(2,6,23,0.55), 0 0 0 2px #10b981 inset',
        outline: '2px solid #10b981',
      }}
      data-testid="tour-spotlight"
    />
  );
};

const TourOverlay = ({ step, setStep, close }) => {
  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <>
      <Spotlight selector={s.selector} />
      <div
        className="fixed z-[100000] bottom-6 right-6 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 p-5 animate-in fade-in slide-in-from-bottom-4 duration-200"
        data-testid="tour-card"
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          data-testid="tour-close-btn"
          aria-label="Close tour"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-bold">
            {step + 1}
          </span>
          <h3 className="font-bold text-base text-slate-900 dark:text-white">{s.title}</h3>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{s.body}</p>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={close}
            className="text-slate-500 hover:text-slate-700"
            data-testid="skip-tour-btn"
          >
            <SkipForward className="w-4 h-4 mr-1" />
            Skip
          </Button>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">{step + 1} of {STEPS.length}</span>
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} data-testid="tour-prev-btn">
                Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => (isLast ? close() : setStep(step + 1))}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="tour-next-btn"
            >
              {isLast ? 'Finish' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

// Wraps the authenticated app so the header button and the tour share state.
export const DemoTour = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const location = useLocation();

  // Close on any route change so the tour can never block navigation.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const start = useCallback(() => {
    setStep(0);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <TourCtx.Provider value={{ start, close, open }}>
      {children}
      {open && <TourOverlay step={step} setStep={setStep} close={close} />}
    </TourCtx.Provider>
  );
};

// Header button that launches the tour on demand.
export const HeaderTourButton = () => {
  const ctx = useContext(TourCtx);
  if (!ctx) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={ctx.start}
      className="hidden lg:inline-flex rounded-xl gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200"
      data-testid="take-tour-btn"
      title="Take a guided tour"
    >
      <Compass className="w-4 h-4" />
      <span className="text-sm font-medium">Take a tour</span>
    </Button>
  );
};

export default DemoTour;
